import type { ThemePagesV6QueryResponse } from '../types/page';
import type { UseQueryOptions } from '@tanstack/react-query';

import { useQuery, useQueryClient } from '@tanstack/react-query';
import { fetchFromAPI } from '~/api/utils';

const queryKey = 'themePageV6';
export type ThemePageV6QueryVariables = {
  where?: {
    idIn?: string[];
  };
};

export const useThemePageV6Query = <TData = ThemePagesV6QueryResponse, TError = unknown>(
  variables?: ThemePageV6QueryVariables,
  options?: UseQueryOptions<ThemePagesV6QueryResponse, TError, TData>,
) => {
  const queryClient = useQueryClient();

  const api = async (variables?: ThemePageV6QueryVariables) => {
    return fetchFromAPI('/api/v6/pages', {
      method: 'POST',
      body: JSON.stringify(variables),
    });
  };

  const fetchManual = async (
    variables?: ThemePageV6QueryVariables,
    isClearCache?: boolean,
  ): Promise<{ data: ThemePagesV6QueryResponse }> => {
    const cacheKey = useThemePageV6Query.getKey(variables);
    const cachedData = queryClient.getQueryData<ThemePagesV6QueryResponse>(cacheKey);

    if (cachedData && !isClearCache) {
      return { data: cachedData };
    }

    const response = (await api(variables)) as ThemePagesV6QueryResponse;
    queryClient.setQueryData(cacheKey, response);
    return { data: response };
  };

  return {
    ...useQuery<ThemePagesV6QueryResponse, TError, TData>(
      useThemePageV6Query.getKey(variables),
      () => api(variables),
      {
        enabled: variables !== undefined,
        ...options,
      },
    ),
    fetchManual,
  };
};

useThemePageV6Query.getKey = (variables?: ThemePageV6QueryVariables) =>
  variables === undefined ? [queryKey] : [queryKey, variables];
